import { useState, useEffect } from "react";
import { Play, X, ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { FILMS } from "../data/films";

const CATEGORIES = ["All", "Film Posters", "Production Stills", "Behind the Scenes", "Cultural Research", "Locations", "Festivals", "Team"];

const IMAGES = [
  { url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=85&w=1600", category: "Locations", caption: "Himalayan ridge — location scout" },
  { url: "https://images.unsplash.com/photo-1526392060635-9d6019884377?auto=format&fit=crop&q=85&w=1600", category: "Cultural Research", caption: "Monastery visit" },
  { url: "https://images.unsplash.com/photo-1727451139462-cd34008cd50b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Behind the Scenes", caption: "On set — Chapter I" },
  { url: "https://images.unsplash.com/photo-1585608996900-c8d3a49328a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Film Posters", caption: "Eternal Flow — poster" },
  { url: "https://images.unsplash.com/photo-1674033846163-fe3ea31c1f97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Film Posters", caption: "Threads of Faith — poster" },
  { url: "https://images.unsplash.com/photo-1482424917728-d82d29662023?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Production Stills", caption: "Halesi — cavern light" },
  { url: "https://images.unsplash.com/photo-1724154353955-525f6e738c0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Cultural Research", caption: "Golden hour — mood" },
  { url: "https://images.unsplash.com/photo-1483347756197-71ef80e95f73?auto=format&fit=crop&q=85&w=1600", category: "Locations", caption: "Snow-line reconnaissance" },
  { url: "https://images.unsplash.com/photo-1470770903676-69b98201ea1c?auto=format&fit=crop&q=85&w=1600", category: "Locations", caption: "River valley" },
  { url: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&q=85&w=1600", category: "Festivals", caption: "Festival research" },
  { url: "https://images.unsplash.com/photo-1760245774056-262c41d54ab4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Team", caption: "Portrait — team" },
  { url: "https://images.unsplash.com/photo-1770452603217-89b4f03e8271?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "Team", caption: "Portrait — team" },
  { url: "https://images.unsplash.com/photo-1516569422572-3b1cea6bdaa4?auto=format&fit=crop&q=85&w=1600", category: "Cultural Research", caption: "Border study" },
];

export default function Gallery() {
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = IMAGES.filter((img) => category === "All" || img.category === category);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + filtered.length) % filtered.length);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, filtered.length]);

  return (
    <main data-testid="gallery-page" className="bg-black pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Trailers & Gallery</p>
        <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter">
          <MaskLines lines={["Frames,", "unstill."]} />
        </h1>
      </section>

      {/* Trailers */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-8">Trailers</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {FILMS.slice(0, 2).map((f, i) => (
            <Reveal key={f.slug} delay={i}
              data-testid={`gallery-trailer-${f.slug}`}
              className="relative aspect-video overflow-hidden bg-[color:var(--dk-muted)] border border-white/10 group">
              <img src={f.poster} alt={f.title} className="film-hover-img w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
                <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">Trailer · Placeholder</p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-overline text-white/50 mb-2">{f.year} · {f.status}</p>
                    <h3 className="font-serif-display text-white text-2xl md:text-3xl">{f.title}</h3>
                  </div>
                  <div className="w-16 h-16 rounded-full border border-[color:var(--dk-gold)] flex items-center justify-center group-hover:bg-[color:var(--dk-red)] transition-colors">
                    <Play size={18} className="text-[color:var(--dk-gold)] group-hover:text-white transition-colors fill-current" />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10">
        <div className="flex flex-wrap gap-3 border-t border-white/10 pt-8">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              data-testid={`gallery-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-[10px] uppercase tracking-overline px-4 py-2 border transition-colors ${
                category === c
                  ? "bg-[color:var(--dk-gold)] text-black border-[color:var(--dk-gold)]"
                  : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Masonry-ish grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-28">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
          {filtered.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              data-testid={`gallery-item-${i}`}
              className="block w-full mb-4 relative group overflow-hidden bg-[color:var(--dk-muted)] break-inside-avoid"
            >
              <img src={img.url} alt={img.caption} loading="lazy" className="film-hover-img w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity text-left">
                <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">{img.category}</p>
                <p className="text-white text-sm mt-1">{img.caption}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-lg flex items-center justify-center p-6 md:p-16"
          onClick={() => setLightbox(null)}
        >
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-white hover:text-[color:var(--dk-gold)] p-2">
            <X size={28} />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + filtered.length) % filtered.length); }}
            className="absolute left-6 md:left-10 text-white hover:text-[color:var(--dk-gold)] p-2">
            <ArrowLeft size={28} />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % filtered.length); }}
            className="absolute right-6 md:right-10 text-white hover:text-[color:var(--dk-gold)] p-2">
            <ArrowRight size={28} />
          </button>
          <img
            src={filtered[lightbox].url}
            alt={filtered[lightbox].caption}
            className="max-h-[85vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">{filtered[lightbox].category}</p>
            <p className="text-white font-serif-display italic text-lg mt-1">{filtered[lightbox].caption}</p>
          </div>
        </div>
      )}
    </main>
  );
}
